/**
 * queue-sort — click-to-sort state and comparator for the queue segment table.
 * Mirrors features/path-banks/lib/path-sort.ts (a {key, dir} state + a toggle +
 * a comparator), but sorts by the column's OWN `sortValue` accessor rather than
 * by a field name: several queue columns are derived, and one of them ("Tested
 * for") renders a string that sorts completely wrong.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { QueueElement } from '@trafficpulse/shared';

import { type QueueSegment } from '../../queue-filters';
import { type QueueColumn } from '../queue-columns';

export type QueueSortDir = 'asc' | 'desc';

export interface QueueSortState {
  /** Column key, or null to keep the order the backend sent. */
  key: string | null;
  dir: QueueSortDir;
}

export const NO_QUEUE_SORT: QueueSortState = { key: null, dir: 'asc' };

/**
 * Per-segment starting sort. Eliminated opens newest-first because the only
 * question asked of that list is "what happened last" — its rows arrive in
 * promotion order (priority, then queuedAt), which is meaningless once an
 * element is dead. Waiting opens the same way for the same reason: nothing
 * about the order it arrives in says which elements landed there recently.
 *
 * Queue is absent on purpose — its order IS the promotion order, and re-sorting
 * it would misrepresent what happens next. Live is absent too: it groups by
 * rule, and its rows are read per section rather than ranked.
 */
export const DEFAULT_QUEUE_SORT: Partial<Record<QueueSegment, QueueSortState>> =
  {
    waiting: { key: 'waitingSince', dir: 'desc' },
    removed: { key: 'removedAt', dir: 'desc' },
  };

export function defaultSortFor(segment: QueueSegment): QueueSortState {
  return DEFAULT_QUEUE_SORT[segment] ?? NO_QUEUE_SORT;
}

/**
 * Which way a column opens on its FIRST click. Dates and counts open desc —
 * "most recent" and "most times" are what you want first; text opens asc.
 * Same convention as features/drops/utils/sort/drops-sort.ts.
 */
export function defaultSortDirFor(column: QueueColumn): QueueSortDir {
  return column.sortDescFirst ? 'desc' : 'asc';
}

/** Same key → flip; a new column → its own natural direction. */
export function toggleQueueSort(
  cur: QueueSortState,
  column: QueueColumn,
): QueueSortState {
  if (cur.key === column.key) {
    return { key: column.key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key: column.key, dir: defaultSortDirFor(column) };
}

/** Blank-ish values compare as "missing" so they can be forced to the end. */
function isMissing(v: string | number | null | undefined): boolean {
  return v === null || v === undefined || v === '';
}

function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, {
    sensitivity: 'base',
  });
}

/**
 * Sort a copy of the rows.
 *
 * Two deliberate choices:
 * - Missing values sink to the bottom in BOTH directions. Flipping the sort to
 *   see the oldest eliminations should not first hand you every row that has no
 *   date at all.
 * - Ties break on bankId, so rows with equal values (very common — whole
 *   batches share an eliminated-at minute) hold a fixed order instead of
 *   jittering between renders.
 */
export function sortQueueRows(
  elements: QueueElement[],
  sort: QueueSortState,
  columns: QueueColumn[],
): QueueElement[] {
  const column = sort.key
    ? columns.find((c) => c.key === sort.key && c.sortValue)
    : undefined;
  if (!column?.sortValue) return elements;
  const valueOf = column.sortValue;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...elements].sort((a, b) => {
    const av = valueOf(a);
    const bv = valueOf(b);
    if (isMissing(av) || isMissing(bv)) {
      if (isMissing(av) && isMissing(bv)) return a.bankId.localeCompare(b.bankId);
      return isMissing(av) ? 1 : -1;
    }
    const diff = compareValues(av as string | number, bv as string | number);
    if (diff !== 0) return diff * dir;
    return a.bankId.localeCompare(b.bankId);
  });
}
